import React from 'react';

import { CrmHomeSection } from '../../../CrmHomeSection/CrmHomeSection';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { CrmHomeBiLogger } from '../../../../bi-logger';
import {
  Container,
  AutoAdjustedColumns,
  Badge,
  Box,
  Button,
  Heading,
  Text,
  AddItem,
} from 'wix-style-react';
import { WORKFLOW_TEMPLATE_IDS, datahooks } from '../../../constants';
import * as css from './WorkflowsSection.scss';
import classNames from 'classnames';
import { WorkflowServiceApi } from '../../../../services/workflows.service';
import { navigateTo, PageComponentId } from '@wix/business-manager-api';

interface TemplateConfig {
  id: string;
  name: string;
  mostUsed: boolean;
  titleKey: string;
  descriptionKey: string;
  isDefault: boolean;
}

const DefaultTemplate = translate()(
  ({ t, onClick }: { t: Function; name: string; onClick: () => any }) => {
    return (
      <TemplateWrapper isDefault>
        <AddItem
          dataHook="start-from-scratch"
          size="small"
          theme="plain"
          onClick={onClick}
        >
          <Box paddingTop="8px" dataHook="start-from-scratch-text">
            {t('workflow.templatePicker.startFromScratch')}
          </Box>
        </AddItem>
      </TemplateWrapper>
    );
  },
);

const WorkflowTemplate = translate()(
  ({
    t,
    title,
    description,
    onClick,
    mostUsed,
    name,
  }: {
    t: Function;
    title?: string;
    description?: string;
    onClick: Function;
    mostUsed?: boolean;
    name: string;
  }) => {
    return (
      <TemplateWrapper mostUsed={mostUsed} onClick={onClick}>
        <Box
          direction="vertical"
          verticalAlign="top"
          align="center"
          padding="0 20px"
        >
          <Box paddingBottom="12px" align="center">
            <Heading dataHook={'template-title-' + name} appearance="H4">
              {title}
            </Heading>
          </Box>
          <Box marginBottom="18px" align="center">
            <Text
              dataHook={'template-description-' + name}
              size="small"
              weight="thin"
            >
              {description}
            </Text>
          </Box>
        </Box>
        <Button
          dataHook={'template-button-' + name}
          as="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClick();
          }}
          skin="light"
          size="small"
        >
          {t('workflow.templatePicker.useTemplate')}
        </Button>
      </TemplateWrapper>
    );
  },
);

export interface TemplateWrapperProps extends InjectedTranslateProps {
  children: any;
  isDefault?: boolean;
  onClick?: Function;
  mostUsed?: boolean;
}

export const TemplateWrapper = translate()(
  ({ t, children, isDefault, onClick, mostUsed }: TemplateWrapperProps) => {
    const clickableTemplateStyle = {
      paddingTop: '36px',
      paddingBottom: '27px',
      paddingLeft: '20px',
      paddingRight: '20px',
    };

    return (
      <Box
        className={classNames(css.templateContainer, {
          [css.defaultTemplateContainer]: isDefault,
        })}
        direction="vertical"
        align="center"
        verticalAlign="space-between"
        display="flex"
      >
        <div
          data-hook="template-clickable-area"
          onClick={() => onClick && onClick()}
          style={{
            height: '100%',
            width: '100%',
            ...(isDefault ? {} : clickableTemplateStyle),
          }}
        >
          {children}
        </div>
        {mostUsed && (
          <div className={css.mostUsedBadgeContainer}>
            <Badge
              dataHook="most-used-badge"
              size="small"
              skin="neutralStandard"
              uppercase={false}
            >
              {t('workflow.templatePicker.mostUsed')}
            </Badge>
          </div>
        )}
      </Box>
    );
  },
);

export const templateConfigs: Partial<TemplateConfig>[] = [
  {
    id: WORKFLOW_TEMPLATE_IDS.SALES,
    name: 'sales',
    mostUsed: true,
    titleKey: 'templates.sales.workflow.name',
    descriptionKey: 'templates.sales.description',
  },
  {
    id: WORKFLOW_TEMPLATE_IDS.TODO,
    name: 'todo',
    titleKey: 'templates.todo.workflow.name',
    descriptionKey: 'templates.todo.description',
  },
  {
    id: WORKFLOW_TEMPLATE_IDS.SITE_MEMBERS,
    name: 'siteMembers',
    titleKey: 'templates.siteMember.workflow.name',
    descriptionKey: 'templates.siteMember.description',
  },
  {
    id: WORKFLOW_TEMPLATE_IDS.SCRATCH,
    name: 'fromScratch',
    isDefault: true,
  },
];

export interface WorkflowsSectionProps extends InjectedTranslateProps {
  biLogger: CrmHomeBiLogger;
  api: WorkflowServiceApi;
}

export const WorkflowsSectionComponent: React.FC<WorkflowsSectionProps> = (
  props,
) => {
  const { t, biLogger, api } = props;

  const onClickTemplate = (template: Partial<TemplateConfig>) => async () => {
    if (template.id === WORKFLOW_TEMPLATE_IDS.SCRATCH) {
      biLogger.reportStartWorkflowFromScratch();
    } else {
      biLogger.reportUseWorkflowTemplate(template.name!);
    }

    const { id } = await api.cloneTemplate(template.id!);
    navigateToWorkflows(id);
  };

  const onClickTitleAction = () => {
    biLogger.reportGoToWorkflows();
    navigateToWorkflows();
  };

  const renderTemplates = (template: Partial<TemplateConfig>, idx) => {
    const templateComponent = template.isDefault ? (
      <DefaultTemplate
        name={template.name!}
        onClick={onClickTemplate(template)}
      />
    ) : (
      <WorkflowTemplate
        onClick={onClickTemplate(template)}
        title={props.t(template.titleKey!)}
        description={props.t(template.descriptionKey!)}
        mostUsed={template.mostUsed}
        name={template.name!}
      />
    );
    return (
      <Box height="100%" width="100%" key={idx}>
        {templateComponent}
      </Box>
    );
  };

  return (
    <CrmHomeSection
      title={t('crmHomePage.workflows.sectionTitle')}
      titleDataHook={datahooks.homeWorkflowsSectionTitle}
      actionOnClick={onClickTitleAction}
      actionTitle={t('crmHomePage.workflows.sectionActionTitle')}
    >
      <Container>
        <AutoAdjustedColumns>
          {templateConfigs.map(renderTemplates)}
        </AutoAdjustedColumns>
      </Container>
    </CrmHomeSection>
  );
};

export const WorkflowsSection = translate()(WorkflowsSectionComponent);

const navigateToWorkflows = (id?: string) => {
  navigateTo({
    pageComponentId: PageComponentId.Workflow,
    contextData: {
      referrer: 'crmHome',
      ...(id ? { appState: id } : {}),
    },
  });
};
